<script setup lang="tsx">
import type { CustomCells, HybridlyRow } from '@/components/table/Table';
import type { AppliedFilters } from '@/components/distiller/types';

import capitalize from 'lodash/capitalize';

import { useHybridlyTable } from '@/hooks/useHybridlyTable';
import { useTableRowClick } from '@/hooks/useTableRowClick';
import { useFilters } from '@/hooks/useFilters';

import EmptyState from '@/components/emptyState/EmptyState.vue';
import IndexPageLayout from '@/components/pageLayout/IndexPageLayout.vue';
import TableBadgesCell from '@/components/table/TableBadgesCell.vue';
import TableDateTimeCell from '@/components/table/TableDateTimeCell.vue';
import TableDualTextCell from '@/components/table/TableDualTextCell.vue';
import TableEnumCell from '@/components/table/TableEnumCell.vue';
import TableRequestDataAttributesCell from '@/components/table/TableRequestDataAttributesCell.vue';
import TabNavDivider from '@/components/tabNav/TabNavDivider.vue';
import TabNavItem from '@/components/tabNav/TabNavItem.vue';

import Building from '@/icons/line/building.svg';

type DataRequestRow = HybridlyRow<App.DataRequests.Data.DataRequestData>;

const props = defineProps<{
  dataRequests: Table<App.DataRequests.Data.DataRequestData>;
  appliedFilters: AppliedFilters;
  distiller: App.Search.Data.DistillerData;
  status: 'pending' | 'approved' | 'rejected';
  counts: {
    pending_count: number;
    approved_count: number;
    rejected_count: number;
  };
}>();

const { table } = useHybridlyTable<App.DataRequests.Data.DataRequestData>(props, 'dataRequests');
const { handleRowClick } = useTableRowClick();
const { encodeFilters } = useFilters();
const encodedFilters = computed(() => encodeFilters(props.appliedFilters));

const customCells = {
  user: ({ row, column }) => (
    <TableDualTextCell
      primaryText={row.value(column.key).name}
      secondaryText={row.value(column.key).email}
    />
  ),
  school: ({ row, column }) => (
    <TableBadgesCell
      badgesToDisplay={1}
      itemIcon={Building}
      items={[row.value(column.key)?.name]}
    />
  ),
  student_grade: ({ row, column }) => <TableEnumCell enumData={row.value(column.key)} />,
  type: ({ row, column }) => (
    <TableBadgesCell badgesToDisplay={1} items={[row.value(column.key)?.label]} />
  ),
  requestable_type: ({ row, column }) => <TableEnumCell enumData={row.value(column.key)} />,
  created_at: ({ row, column }) => <TableDateTimeCell date={row.value(column.key)} />,
  attributes: ({ row, column }) => (
    <TableRequestDataAttributesCell attributes={row.value(column.key)} />
  )
} as CustomCells<App.DataRequests.Data.DataRequestData>;

function handleDataRequestRowClick(event: MouseEvent, row: DataRequestRow) {
  const requestableType = row.value('requestable_type') as App.Base.Data.EnumData;
  const type = requestableType.value as string;

  let url = '' as string;
  switch (type) {
    case 'application':
      url = route('data-requests.college-application.edit', { dataRequest: row.key });
      break;
    case 'financial_aid':
      url = route('data-requests.financial-aid.edit', { dataRequest: row.key });
      break;
    case 'test':
      url = route('data-requests.test.edit', { dataRequest: row.key });
      break;
    case 'student_scholarship':
      url = route('data-requests.scholarship.edit', { dataRequest: row.key });
      break;
    default:
      return;
  }

  handleRowClick(url, event);
}
</script>


					<script lang="tsx">
					import __hybridly_layout_0 from '~/resources/layouts/default.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <IndexPageLayout
    title="Data Requests"
    :table="table"
    :appliedFilters="appliedFilters"
    :distiller="distiller"
    :customCells="customCells"
    :selectionsEnabled="false"
    :stickyStartColumns="1"
    clickableRows
    @clickRow="($event, row) => handleDataRequestRowClick($event, row as DataRequestRow)"
  >
    <template #tabs>
      <TabNavItem
        :badgeLabel="counts.pending_count"
        :isActive="status === 'pending'"
        label="Pending"
        variant="inline"
        :href="
          route('data-requests.index', {
            status: 'pending',
            ...(encodedFilters ? { filters: encodedFilters } : {})
          })
        "
      />
      <TabNavDivider layout="inline" />
      <TabNavItem
        :badgeLabel="counts.approved_count"
        :isActive="status === 'approved'"
        label="Approved"
        variant="inline"
        :href="
          route('data-requests.index', {
            status: 'approved',
            ...(encodedFilters ? { filters: encodedFilters } : {})
          })
        "
      />
      <TabNavDivider layout="inline" />
      <TabNavItem
        :badgeLabel="counts.rejected_count"
        :isActive="status === 'rejected'"
        label="Rejected"
        variant="inline"
        :href="
          route('data-requests.index', {
            status: 'rejected',
            ...(encodedFilters ? { filters: encodedFilters } : {})
          })
        "
      />
    </template>
    <template #emptyState>
      <EmptyState
        :title="`No ${status} data requests found`"
        :description="`${capitalize(status)} data requests will appear on this screen`"
      />
    </template>
  </IndexPageLayout>
</template>
